/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import error from '../../../../common/nodejs/errors';
import config from '../../../../share/config';
import session from '../../../../share/session';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';

/** 各フェーズ定義 */
const PHASE = {
  START: "start",
  EXECUTE: "execute",
  SUCCESS: "success",
  CANCEL: "cancel",
  TIMEOUT: "timeout",
  FAILED: "failed"
};

/** API type定義 */
const API_TYPE = {
  STANDBY: 'standBy',
  INITIALIZE: 'initialize',
  START: 'start',
  STOP: 'stop',
  TERMINATE: 'terminate',
};

export default {
  name: "requirement061",
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      displayInfo: "",
      modal_requirement_061: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: "", required: true },
    workSupportId: { type: Number, default: "", required: true }
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED:
        case PHASE.TIMEOUT:
          return "danger";
        case PHASE.SUCCESS:
          return "success";
        case PHASE.CANCEL:
        case PHASE.START:
        case PHASE.EXECUTE:
        default:
          return "secondary";
      }
    }
  },

  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),

    /** 初期化処理 */
    init() {
      this.modal_requirement_061 = true;
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit("close-wizard");
      this.modal_requirement_061 = false;
    },

    /** 調整値クリア実施 */
    async clearAdjustmentValue() {
      try {
        this.isLoading = true;

        // 待機処理
        if (!await this.doStandby()) {
          return;
        }

        // 初期化処理
        if (!await this.doInitialize()) {
          return;
        }

        // 開始処理
        await this.doStart();

      } finally {
        this.isLoading = false;
      }
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 通信エラー、タイムアウトエラー、正常終了画面の場合は、ウィザードを閉じる
      if (this.phase === PHASE.FAILED || this.phase === PHASE.TIMEOUT || this.phase === PHASE.SUCCESS) {
        this.closeWizard();
        return;
      }
      // 異常終了：通信エラー表示
      this.phase = PHASE.FAILED;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },
    
    /** キャンセル表示画面でキャンセル押下時の処理 */
    checkCancel() {
      // 初期画面へ遷移
      this.phase = PHASE.START;
    },

    /** 待機処理 */
    async doStandby() {
      // API実行パラメータ作成
      const systemId = individualDiagnose.getCurrentSystemId(this);
      const token = session.getToken();
      const requestBody = {
        id_token: token.id_token,
        type: API_TYPE.STANDBY
      };

      // standBy API実行
      const response = await individualWorkSupportUtil.pollingWspApi(
        this,
        this.netAppId,
        systemId,
        this.workSupportId,
        requestBody,
        // エラー発生のコールバック
        (errorType) => { errorType; this.showUnexpectedError(); },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => { this.validateErrorResult(result); },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => { this.validateErrorResult(result); },
      );

      let isSuccess = false;

      if (response) {
        if (response.data.status === config.STATUS_WSP_COMPLETED) {
          // 正常終了
          isSuccess = true;
        } else {
          // 通信エラー
          this.showUnexpectedError();
        }
      }

      return isSuccess;
    },

    /** 初期化処理 */
    async doInitialize() {
      // API実行パラメータ作成
      const systemId = individualDiagnose.getCurrentSystemId(this);
      const token = session.getToken();
      const requestBody = {
        id_token: token.id_token,
        type: API_TYPE.INITIALIZE
      };

      // initialize API実行
      const response = await individualWorkSupportUtil.pollingWspApi(
        this,
        this.netAppId,
        systemId,
        this.workSupportId,
        requestBody,
        // エラー発生のコールバック
        (errorType) => { errorType; this.showUnexpectedError(); },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => { this.validateErrorResult(result); },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => { this.validateErrorResult(result); },
      );

      let isSuccess = false;

      if (response) {
        switch (response.data.status) {
          case config.STATUS_WSP_COMPLETED:
            // 正常終了
            this.phase = PHASE.EXECUTE;
            isSuccess = true;
            break;
          case config.STATUS_WSP_FAILED:
            switch (response.data.async_code) {
              case error.WSP_061_TIMEOUT_ERROR:
                // タイムアウトエラー
                this.phase = PHASE.TIMEOUT;
                break;
              case error.WSP_061_COMMUNICATION_ERROR:
                // 通信エラー
                this.showUnexpectedError();
                break;
              default:
                // 通信エラー
                this.showUnexpectedError();
                break;
            }
            break;
          default:
            // 通信エラー
            this.showUnexpectedError();
            break;
        }
      }

      return isSuccess;
    },

    /** 開始処理 */
    async doStart() {
      // API実行パラメータ作成
      const systemId = individualDiagnose.getCurrentSystemId(this);
      const token = session.getToken();
      const requestBody = {
        id_token: token.id_token,
        type: API_TYPE.START
      };

      // start API実行
      const response = await individualWorkSupportUtil.pollingWspApi(
        this,
        this.netAppId,
        systemId,
        this.workSupportId,
        requestBody,
        // エラー発生のコールバック
        (errorType) => { errorType; this.showUnexpectedError(); },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => { this.validateErrorResult(result); },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => { this.validateErrorResult(result); },
      );

      if (response) {
        switch (response.data.status) {
          case config.STATUS_WSP_COMPLETED:
            // 正常終了：読み出しデータ表示
            this.displayInfo = this.convertRoutineStatusToMessage(response.data.user_data.result_code);
            if (this.displayInfo.length !== 0) {
              this.phase = PHASE.SUCCESS;
            } else {
              // 通信エラー
              this.showUnexpectedError();
            }
            break;
          case config.STATUS_WSP_FAILED:
            switch (response.data.async_code) {
              case error.WSP_061_TIMEOUT_ERROR:
                // タイムアウトエラー
                this.phase = PHASE.TIMEOUT;
                break;
              case error.WSP_061_COMMUNICATION_ERROR:
                // 通信エラー
                this.showUnexpectedError();
                break;
              default:
                // 通信エラー
                this.showUnexpectedError();
                break;
            }
            break;
          default:
            // 通信エラー
            this.showUnexpectedError();
            break;
        }
      }
    },

    /** 停止処理 */
    async doStop() {
      try {
        this.isLoading = true;

        // API実行パラメータ作成
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        const requestBody = {
          id_token: token.id_token,
          type: API_TYPE.STOP
        };

        // stop API実行
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          requestBody,
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // 処理結果にかかわらず、terminateを実行する
          await this.doTerminateMain();
        }

      } finally {
        this.isLoading = false;
      }
    },

    /** 終了処理 */
    async doTerminate() {
      try {
        this.isLoading = true;

        await this.doTerminateMain();

      } finally {
        this.isLoading = false;
      }
    },

    /** 終了処理 */
    async doTerminateMain() {
      // API実行パラメータ作成
      const systemId = individualDiagnose.getCurrentSystemId(this);
      const token = session.getToken();
      const requestBody = {
        id_token: token.id_token,
        type: API_TYPE.TERMINATE
      };

      // terminate API実行
      const response = await individualWorkSupportUtil.pollingWspApi(
        this,
        this.netAppId,
        systemId,
        this.workSupportId,
        requestBody,
        // エラー発生のコールバック
        (errorType) => { errorType; this.showUnexpectedError(); },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => { this.validateErrorResult(result); },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => { this.validateErrorResult(result); },
      );

      if (response) {
        // 処理結果にかかわらず、ウィザードを閉じる
        this.closeWizard();
      }
    },

    /**
     * ルーチンステータスのコードを文字列に変換する
     * @param {string}  resultCode ルーチンステータス
     */
    convertRoutineStatusToMessage(resultCode) {
      let wordCode = undefined;
      switch (resultCode) {
        case "1002":
          wordCode = "REQ_WS_61_TXT_3_1002";
          break;
        case "1F00":
          wordCode = "REQ_WS_61_TXT_3_1F00";
          break;
        case "1F01":
          wordCode = "REQ_WS_61_TXT_3_1F01";
          break;
        default:
          break;
      }
      if (wordCode) {
        return this.$t(`${this.i18nWsKey}.` + wordCode);
      } else {
        return "";
      }
    },

    // ユーザ操作
    /** 初期画面 OKボタン押下 */
    onStartOkClicked() {
      this.clearAdjustmentValue();
    },

    /** 初期画面 キャンセルボタン押下 */
    onStartCancelClicked() {
      // キャンセル表示画面へ遷移
      this.phase = PHASE.CANCEL;
    },

    /** キャンセル確認画面 OKボタン押下 */
    onCancelOkClicked() {
      this.closeWizard();
    },

    /** キャンセル確認画面 キャンセルボタン押下 */
    onCancelCancelClicked() {
      // 初期画面へ遷移
      this.phase = PHASE.START;
    },

    /** 正常終了画面 OKボタン押下 */
    onSuccessOkClicked() {
      this.doStop();
    },

    /** タイムアウトエラー画面 OKボタン押下 */
    onTimeoutErrorOkClicked() {
      this.doTerminate();
    },

    /** 通信エラー画面 OKボタン押下 */
    onCommunicationErrorOkClicked() {
      this.doTerminate();
    }
  },

  async mounted() {
    this.init();
  }
};
